import React, { useCallback, useEffect, useMemo } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useAppDispatch, useAppSelector } from 'app/store';
import {
  getChangePasswordLink,
  getPasswordPolicy,
} from 'app/store/Auth/selectors';
import {
  changePasswordEffect,
  fetchPasswordPolicyEffect,
} from 'app/store/Auth/effects';
import { PasswordChangeFormLoader } from 'app/components/Settings/PasswordChangeFormLoader';
import { ApiErrorCodeMessages } from 'types/enums';
import { PasswordPolicy } from 'types/auth';
import { dynamicYupResolver } from 'app/utils/utils';
import { PasswordChangeFormData } from 'app/components/Settings/PasswordChangeDialog/utils';
import * as yup from 'yup';
import { useFetchValidationSchema, useResource } from 'app/utils/hooks';
import { transformSchemaToYup } from 'app/utils/schemaToYup';
import merge from 'lodash/merge';
import {
  Button,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  SimpleGrid,
  Text,
} from '@chakra-ui/react';
import { PasswordChangeFormFields } from '../PasswordChangeFormFields';
import { errorMessages } from './utils';
import { useModalProps } from '../useModalProps';

const defaultValues = {
  currentPassword: '',
  newPassword: '',
  newPasswordRepeat: '',
};

interface PasswordChangeDialogProps {
  open: boolean;
  onClose: () => void;
}

export interface PasswordChangeDialogComponentProps {
  open: boolean;
  onClose: () => void;
  changePassword: (values: PasswordChangeFormData) => Promise<void>;
  passwordPolicy?: PasswordPolicy;
  validationSchema?: yup.AnyObjectSchema;
  isLoading?: boolean;
}

export const PasswordChangeDialogComponent: React.FC<PasswordChangeDialogComponentProps> = ({
  open,
  onClose,
  changePassword,
  passwordPolicy,
  validationSchema,
  isLoading,
}) => {
  const form = useForm<PasswordChangeFormData>({
    defaultValues,
    mode: 'all',
    context: {
      yupValidationSchema: validationSchema,
    },
    resolver: dynamicYupResolver,
  });

  const {
    handleSubmit,
    setError,
    reset,
    formState: { isSubmitting, isValid },
  } = form;

  const onSubmit = async (values: PasswordChangeFormData) => {
    try {
      await changePassword(values);
      onClose();
    } catch (e) {
      setError('currentPassword', {
        message: ApiErrorCodeMessages.BAD_PASSWORD,
      });
    }
  };

  useEffect(() => {
    if (open) {
      reset();
    }
  }, [open, reset]);

  const { modalProps, contentProps } = useModalProps(open);

  return (
    <Modal isOpen={open} onClose={onClose} {...modalProps}>
      <ModalOverlay />
      <ModalContent {...contentProps}>
        <FormProvider {...form}>
          <form
            onSubmit={handleSubmit(onSubmit)}
            data-testid="change-password-form"
          >
            <ModalHeader data-testid="dialog-title" title="Change Password">
              Change Password
              <Text fontSize="xs" color="M.60">
                Changing your password will sign you out on all other devices.
              </Text>
            </ModalHeader>
            <ModalBody overscrollBehavior="none">
              {isLoading ? (
                <PasswordChangeFormLoader />
              ) : (
                <SimpleGrid columns={1} spacing={4}>
                  <PasswordChangeFormFields passwordPolicy={passwordPolicy} />
                </SimpleGrid>
              )}
            </ModalBody>
            <ModalFooter>
              <Button onClick={onClose} variant="secondary">
                Cancel
              </Button>
              <Button
                isDisabled={isSubmitting || !isValid}
                type="submit"
                data-testid="PasswordChangeDialog-change-btn"
                ml={2}
                variant="primary"
              >
                Change
              </Button>
            </ModalFooter>
          </form>
        </FormProvider>
      </ModalContent>
    </Modal>
  );
};

export const PasswordChangeDialog: React.FC<PasswordChangeDialogProps> = ({
  open,
  onClose,
}) => {
  const dispatch = useAppDispatch();
  const changePasswordLink = useAppSelector(getChangePasswordLink);
  const [passwordPolicy, loading] = useResource(
    fetchPasswordPolicyEffect,
    getPasswordPolicy,
    { shouldFetch: open }
  );

  const changePassword = useCallback(
    (values: PasswordChangeFormData) =>
      dispatch(
        changePasswordEffect({
          currentPassword: values.currentPassword,
          newPassword: values.newPasswordRepeat,
        })
      ).unwrap(),
    [dispatch]
  );

  const {
    validationSchema,
    validationSchemaFetched,
  } = useFetchValidationSchema({ link: changePasswordLink });

  const mergedValidationSchema = useMemo(() => {
    if (validationSchema && passwordPolicy) {
      return merge(validationSchema, {
        properties: { newPassword: { ...passwordPolicy } },
      });
    }
    return undefined;
  }, [validationSchema, passwordPolicy]);

  const yupValidationSchema = useMemo(
    () =>
      mergedValidationSchema &&
      transformSchemaToYup(
        mergedValidationSchema,
        defaultValues,
        errorMessages
      ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [mergedValidationSchema, errorMessages]
  );

  const concatenatedSchema = yup
    .object()
    .shape({
      newPasswordRepeat: yup
        .string()
        .required(errorMessages.errors.newPasswordRepeat.required)
        .oneOf(
          [yup.ref('newPassword'), null],
          errorMessages.errors.newPasswordRepeat.match
        ),
    })
    .concat(yupValidationSchema);

  return (
    <PasswordChangeDialogComponent
      open={open}
      onClose={onClose}
      changePassword={changePassword}
      passwordPolicy={passwordPolicy}
      validationSchema={concatenatedSchema}
      isLoading={!validationSchemaFetched || loading}
    />
  );
};
